import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';

interface ReauthModalProps {
  isOpen: boolean;
  phoneNumber: string;
  onCodeSubmit: (code: string) => void;
  onCancel: () => void;
  isVerifying: boolean;
}

export function ReauthModal({
  isOpen,
  phoneNumber,
  onCodeSubmit,
  onCancel,
  isVerifying,
}: ReauthModalProps) {
  const [code, setCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6) {
      onCodeSubmit(code);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={onCancel}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div
              className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="reauth-modal"
            >
              <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4">
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mb-4">
                      <svg
                        className="w-6 h-6 text-primary-600 dark:text-primary-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Verify Your Identity
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      We've sent a verification code to{' '}
                      <span className="font-mono font-medium">{phoneNumber}</span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={code}
                      onChange={handleCodeChange}
                      placeholder="000000"
                      data-testid="verification-code-input"
                      className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                      autoFocus
                      disabled={isVerifying}
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-3">
                    <p className="text-xs text-blue-900 dark:text-blue-300">
                      This verification is required for security when deleting your account.
                    </p>
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onCancel}
                    disabled={isVerifying}
                    className="flex-1"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={code.length !== 6 || isVerifying}
                    isLoading={isVerifying}
                    className="flex-1"
                    data-testid="verify-button"
                  >
                    {isVerifying ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
